import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, BRAND_FULL_NAME, MAIN_SITE_URL } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopGuideOpen, setShopGuideOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);
  const shopRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shopRef.current && !shopRef.current.contains(e.target as Node)) {
        setShopGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileShopOpen(false);
      setMobileArticlesOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
              <span className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
                {BRAND_FULL_NAME}
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Home
              </Link>

              <div ref={shopRef} className="relative">
                <button
                  onClick={() => setShopGuideOpen(!shopGuideOpen)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  Shopping Guide
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${shopGuideOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                  </span>
                </button>

                {shopGuideOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden animate-fadeIn">
                    <div className="max-h-72 overflow-y-auto py-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={() => setShopGuideOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={`${cat.icon} text-sm`}></i>
                          </span>
                          <span className="whitespace-nowrap">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('seo-guides');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                SEO Guides
              </Link>

              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="ml-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>

          <div className="absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-background-50 shadow-2xl overflow-y-auto animate-slideInRight">
            <div className="flex items-center justify-between p-4 border-b border-background-200/70">
              <span className="font-heading text-lg font-bold text-foreground-900">{BRAND_FULL_NAME}</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <button
                onClick={() => setMobileShopOpen(!mobileShopOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-bag-3-line"></i>
                  </span>
                  Shopping Guide
                </span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${mobileShopOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                </span>
              </button>

              {mobileShopOpen && (
                <div className="ml-4 pl-4 border-l-2 border-primary-200 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <button
                onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-article-line"></i>
                  </span>
                  SEO Guides
                </span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${mobileArticlesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                </span>
              </button>

              {mobileArticlesOpen && (
                <div className="ml-4 pl-4 border-l-2 border-primary-200 space-y-0.5 max-h-56 overflow-y-auto">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/${article.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-xs"></i>
                      </span>
                      <span className="line-clamp-1">{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200/70 my-3"></div>

              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-store-2-line"></i>
                </span>
                Visit Main Store
              </a>

              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 mt-3 px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line"></i>
                </span>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}